/* eslint-disable import/no-unassigned-import */
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import { A11y, Keyboard, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { type Swiper as SwiperClass } from 'swiper/types';

import { Breakpoint, Image, PureButton, useBreakpoint } from '@jsmdg/yoshi';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import { type Image as ImageType, type ProductSearchImageItem } from '../../../shared/types';
import { trackImageSlide, trackThumbnailClicked } from './imageGallery.tracking';
import { ImageGalleryControl } from './ImageGalleryControl';
import styles from './ImageGallery.module.scss';

const getSlidingDirection = (previousIndex: number, currentIndex: number): string => {
    return previousIndex < currentIndex ? 'Slide next' : 'Slide previous';
};

const messages = defineMessages({
    slideAnnouncement: {
        defaultMessage: 'Bild {current} von {total}',
    },
    thumbnailAriaLabel: {
        defaultMessage: 'Vorschaubild {index} von {total}, {altText}, Schaltfläche',
    },
});

type ImageGalleryProps = {
    readonly title: string;
    readonly images: ImageType[] | ProductSearchImageItem[];
};

const ImageGallery = ({ images, title }: ImageGalleryProps): JSX.Element => {
    const intl = useIntl();
    const [swiper, setSwiper] = useState<null | SwiperClass>(null);
    const galleryRef = useRef<SwiperClass>();
    const isDesktopViewport = useBreakpoint(Breakpoint.SM);
    const isThumbnailClicked = useRef(false);
    const [activeSlide, setActiveSlide] = useState<number>(
        (galleryRef.current?.realIndex || 0) + 1,
    );
    const [isMouseEntering, setIsMouseEntering] = useState(false);

    function handleNavChange(swiperClass: SwiperClass): void {
        const clickedImageURL =
            galleryRef.current?.slides[swiperClass.activeIndex]
                ?.querySelector('img')
                ?.getAttribute('src') || '';

        const decodedURL = decodeURIComponent(clickedImageURL);
        const truncatedURL = decodedURL.slice(decodedURL.indexOf('/assets/') || 0);

        if (isThumbnailClicked.current) {
            trackThumbnailClicked(truncatedURL);
        } else {
            const label = getSlidingDirection(swiperClass.previousIndex, swiperClass.activeIndex);
            trackImageSlide(label, truncatedURL);
        }

        isThumbnailClicked.current = false;
        setActiveSlide(swiperClass.activeIndex + 1);
        galleryRef.current?.update();
    }

    return (
        <div>
            <span aria-live="polite" aria-atomic="true" className="screenReaderOnly">
                {intl.formatMessage(messages.slideAnnouncement, {
                    current: activeSlide,
                    total: images.length,
                })}
            </span>
            <Swiper
                className={classNames('w-100 mx-auto', styles.gallerySwiper)}
                onSwiper={(gallerySwiper: SwiperClass) => {
                    galleryRef.current = gallerySwiper;
                }}
                pagination={{
                    clickable: true,
                    bulletClass: styles.paginationBullet,
                    bulletActiveClass: styles.paginationBulletActive,
                    horizontalClass: styles.paginationContainer,
                    modifierClass: styles.paginationContainer,
                }}
                thumbs={{
                    swiper,
                }}
                modules={[A11y, Keyboard, Thumbs, Pagination]}
                watchOverflow
                onSlideChangeTransitionEnd={swiperInstance => handleNavChange(swiperInstance)}
                keyboard={{ enabled: true, onlyInViewport: true }}
                a11y={{
                    enabled: true,
                    id: 'swiper-wrapper-id',
                }}
            >
                {images.map((image, index) => (
                    <SwiperSlide
                        key={`${image.src}-${String(index)}-gallery-image`}
                        className={styles.swiperSlide}
                        role="group"
                        aria-hidden={activeSlide - 1 !== index}
                        tabIndex={activeSlide - 1 === index ? 0 : -1}
                        onFocus={() => {
                            setIsMouseEntering(true);
                        }}
                    >
                        <Image
                            src={image.src}
                            alt={`${image.alt || title} - ${index + 1}`}
                            srcSet={image.srcset}
                            sizes={image.sizes}
                            className={classNames('w-100 h-100 d-block object-fit-cover')}
                            lazyLoad={false}
                            fetchpriority={index === 0 ? 'high' : 'low'}
                            width={714}
                            height={470}
                        />
                    </SwiperSlide>
                ))}
                <div
                    slot="container-end"
                    onMouseEnter={() => {
                        setIsMouseEntering(true);
                    }}
                    onMouseLeave={() => {
                        setIsMouseEntering(false);
                    }}
                    className={classNames(
                        'position-absolute d-flex justify-content-center',
                        styles.imageGalleryControlContainer,
                    )}
                >
                    <ImageGalleryControl
                        show={isMouseEntering && isDesktopViewport}
                        currentSlide={activeSlide}
                        totalSlides={galleryRef.current?.slides.length || 1}
                        onNext={() => galleryRef.current?.slideNext()}
                        onPrev={() => galleryRef.current?.slidePrev()}
                        isDisabledNext={galleryRef.current?.isEnd || false}
                        isDisabledPrev={galleryRef.current?.isBeginning || false}
                    />
                </div>
            </Swiper>
            <Swiper
                className={styles.thumbnailGallery}
                slidesPerView={4}
                spaceBetween={16}
                roundLengths
                hidden={!isDesktopViewport}
                threshold={100}
                watchSlidesProgress
                modules={[Thumbs]}
                onSwiper={thumbs => {
                    flushSync(() => {
                        setSwiper(thumbs);
                    });
                }}
                onClick={() => {
                    isThumbnailClicked.current = true;
                }}
            >
                {images.map((image, index) => (
                    <SwiperSlide
                        key={`${image.src}-${String(index)}-thumbnail`}
                        tabIndex={-1}
                        onFocus={() => galleryRef.current?.slideTo(index)}
                    >
                        <PureButton
                            onClick={() => galleryRef.current?.slideTo(index)}
                            className={classNames(
                                styles.thumbnailSlide,
                                {
                                    [styles.activeThumbnailSlide]: index + 1 === activeSlide,
                                },
                                'swiper-slide swiper-slide-active swiper-slide-thumb-active swiper-slide-visible swiper-slide-fully-visible',
                            )}
                            aria-label={intl.formatMessage(messages.thumbnailAriaLabel, {
                                index: index + 1,
                                total: images.length,
                                altText: image.alt || title,
                            })}
                            tabIndex={0}
                        >
                            <Image
                                src={(image as ImageType).thumbnail || image.src}
                                alt={`${image.alt || title} - ${index + 1}`}
                                className={classNames(
                                    'w-100 h-100 d-block object-fit-cover',
                                    styles.thumbImage,
                                )}
                                width={145}
                                height={95}
                                aria-hidden="true"
                            />
                        </PureButton>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};

export { ImageGallery };

export default ImageGallery;
